<template>
    <base-dropdown
        placement="bottom"
        :fill="true"
        @open="onOpen"
    >
        <button
            slot="trigger"
            slot-scope="{
                isOpen,
                toggle,
                open,
                close
            }"
            ref="button"
            :disabled="disabled"
            :autofocus="autofocus"
            :class="[{
                'is-invalid' : $validator.has(validatorName, sid),
                'is-disabled': disabled,
                'is-small': isSmall,
                'is-dark': isDark
            }, inputClass]"
            class="pr-10 text-left input"
            type="button"
            @click.prevent="toggle"
            @keydown.down.prevent="isOpen ? highlightNext() : open()"
            @keydown.up.prevent="isOpen ? highlightPrev() : open()"
            @keydown.enter.prevent="isOpen ? selectHighlighted(close) : null"
            @keydown.space.prevent="isOpen ? null : open()"
        >
            <template v-if="selected">
                {{ selected.label }}
            </template>

            <span
                v-else
                class="opacity-50"
            >
                {{ placeholder }}
            </span>

            <div class="absolute inset-y-0 right-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                <base-svg
                    :name="isOpen ? 'icon-cheveron-up' : 'icon-cheveron-down'"
                    :key="isOpen ? 'icon-cheveron-up' : 'icon-cheveron-down'"
                    class="w-6 h-6 -mt-1"
                />
            </div>
        </button>
        <div
            slot="menu"
            slot-scope="{
                isOpen,
                close
            }"
            ref="options"
            class="w-full py-2 overflow-y-auto dropdown-menu mt-3px max-h-xs scrollbar-hidden"
            :class="_.kebabCase(name + '-dropdown-menu')"
        >
            <div
                v-if="canSearch"
                class="px-2 mb-1"
            >
                <input
                    v-model="search"
                    type="text"
                    ref="search"
                    class="w-full px-2 pt-2 pb-1 text-sm rounded bg-grey-dark focus:outline-none focus:shadow-outline"
                    @input="$emit('search', search)"
                    @keydown.down.prevent="highlightNext"
                    @keydown.up.prevent="highlightPrev"
                    @keydown.enter.prevent="isOpen ? selectHighlighted(close) : null"
                >
            </div>

            <div
                v-for="(option, index) in options"
                :key="index"
                :class="{
                    'is-active': index === highlighted_index,
                    'is-focused': optionValue(option) === value
                }"
                class="overflow-x-hidden truncate dropdown-menu-link"
                @click.prevent="select(option, close)"
            >
                {{ optionLabel(option) }}

                <div
                    v-if="option.sublabel"
                    class="overflow-x-hidden text-xs truncate text-grey-light"
                >
                    {{ option.sublabel }}
                </div>
            </div>
        </div>
    </base-dropdown>
</template>

<script>
    import _ from 'lodash'

    export default {
        props: {
            value: {
                validator: value => true,
                default: null
            },
            options: {
                type: Array,
                required: true
            },
            sid: {
                type: String,
                default: () => _.uuid()
            },
            name: {
                type: String,
                required: true
            },
            validatorName: {
                type: String,
                required: false,
                default () {
                    return this.name.replace('[', '.').replace(']', '')
                }
            },
            placeholder: {
                type: String,
                default: ''
            },
            autofocus: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            },
            canSearch: {
                type: Boolean,
                default: false
            },
            isSmall: {
                type: Boolean,
                default: false
            },
            isDark: {
                type: Boolean,
                default: false
            },
            inputClass: {
                type: String,
                default: ''
            }
        },
        data () {
            return {
                search: '',
                highlighted_index: -1,
                selected: null
            }
        },
        created () {
            this.updateSelected()
        },
        watch: {
            value () {
                this.updateSelected()
            }
        },
        methods: {
            updateSelected () {
                if (!_.isNull(this.value)) {
                    this.selected = _.find(this.options, { value: this.value })
                }
            },
            optionLabel (option) {
                return _.get(option, 'label', option)
            },
            optionValue (option) {
                return _.get(option, 'value', option)
            },
            valueLabel (value) {
                return _.get(_.find(this.options, { value }), 'label', value)
            },
            select (option, close) {
                this.$emit('input', this.optionValue(option))

                close()

                this.highlight(0)

                this.highlighted_index = -1

                this.$refs.button.focus()
            },
            selectHighlighted (close) {
                this.select(this.options[this.highlighted_index], close)
            },
            highlightNext () {
                this.highlight(this.highlighted_index + 1)
            },
            highlightPrev () {
                this.highlight(this.highlighted_index - 1)
            },
            highlight (index) {
                this.highlighted_index = index

                if (this.highlighted_index < 0) {
                    this.highlighted_index = this.options.length - 1
                }

                if (this.highlighted_index > this.options.length - 1) {
                    this.highlighted_index = 0
                }

                this.$refs.options.children[this.highlighted_index].scrollIntoView({
                    block: 'nearest'
                })
            },
            onOpen () {
                if (this.canSearch) {
                    this.$nextTick(() => this.$refs.search.focus())
                }

                this.$emit('open')
            }
        }
    }
</script>
